import {
  CampaignImpressionsByLocationRO,
  CampaignTopicsByLocationRO,
  GetProgrammaticCampaignAccountsInBuyerResearchStageRO,
  GetProgrammaticCampaignAccountsLiftRO,
  GetProgrammaticCampaignBuyerResearchStageEngagementRO,
  GetProgrammaticCampaignCreativeEngagementTestingRO,
  GetProgrammaticCampaignCreativesEngagementsRO,
  GetProgrammaticCampaignFatigueMapRO,
  GetProgrammaticCampaignIntegratedCampaignReportRO,
  GetProgrammaticCampaignMobileDeliveryBreakdownRO,
  GetProgrammaticCampaignOverallVideoViewsRO,
  GetProgrammaticCampaignPerformanceByDeviceRO,
  GetProgrammaticCampaignStatisticsRO,
  GetProgrammaticCampaignTopAccountsRO,
  GetProgrammaticCampaignVideoCompletionRateRO,
  SiteEngagementLiftRO,
} from "@intentsify/dto";
import { apiService, Endpoints } from "api";
import {
  DisplayPerformanceFilters,
  DisplayPerformanceRequests,
} from "../DisplayPerformanceShared";

const IP = Endpoints.ProgrammaticCampaigns.InnovidPerformance.Get;

const withFiltersParams = (filters: DisplayPerformanceFilters) => ({
  params: {
    yearFrom: filters.yearFrom,
    yearTo: filters.yearTo,
    weekFrom: filters.weekFrom,
    weekTo: filters.weekTo,
  },
});

// Campaign Engagement requests

const getStatistics = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignStatisticsRO> => {
  const { data } = await apiService.get(
    IP.Statistics(id),
    withFiltersParams(filters)
  );
  return data;
};

const getImpressionsByLocation = async (
  id: string | number,
  filters: DisplayPerformanceFilters
): Promise<CampaignImpressionsByLocationRO> => {
  const { data } = await apiService.get(
    IP.ImpressionsByLocation(id),
    withFiltersParams(filters)
  );
  return data;
};

const getTopicsByLocation = async (
  id: string | number,
  filters: DisplayPerformanceFilters
): Promise<CampaignTopicsByLocationRO> => {
  const { data } = await apiService.get(
    IP.TopicsByLocation(id),
    withFiltersParams(filters)
  );
  return data;
};

const getPerformanceByDevice = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignPerformanceByDeviceRO> => {
  const { data } = await apiService.get(
    IP.PerformanceByDevice(id),
    withFiltersParams(filters)
  );
  return data;
};

const getMobileDeliveryBreakdown = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignMobileDeliveryBreakdownRO> => {
  const { data } = await apiService.get(
    IP.MobileDeliveryBreakdown(id),
    withFiltersParams(filters)
  );
  return data;
};

const getSiteEngagementLift = async (
  id: string
): Promise<SiteEngagementLiftRO> => {
  const { data } = await apiService.get(IP.SiteEngagementLift(id));
  return data;
};

// Accounts Performance requests

const getAccountLifts = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignAccountsLiftRO> => {
  const { data } = await apiService.get(
    IP.AccountLifts(id),
    withFiltersParams(filters)
  );
  return data;
};

const getAccountInBuyerResearchStage = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignAccountsInBuyerResearchStageRO> => {
  const { data } = await apiService.get(
    IP.AccountsInBuyerResearchStage(id),
    withFiltersParams(filters)
  );
  return data;
};

const getTopAccounts = async (
  id: string | number,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignTopAccountsRO> => {
  const { data } = await apiService.get(
    IP.TopAccounts(id),
    withFiltersParams(filters)
  );
  return data;
};

const getIntegratedCampaignReport = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignIntegratedCampaignReportRO> => {
  const { data } = await apiService.get(
    IP.IntegratedCampaignReport(id),
    withFiltersParams(filters)
  );
  return data;
};

// Creative Engagement requests

const getCreativeWithHighestEngagement = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignCreativesEngagementsRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignCreativesEngagementsRO>(
      IP.CreativeWithHighestEngagement(id),
      withFiltersParams(filters)
    );
  return data;
};

const getBuyerResearchStageEngagement = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignBuyerResearchStageEngagementRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignBuyerResearchStageEngagementRO>(
      IP.BuyerResearchStageEngagement(id),
      withFiltersParams(filters)
    );
  return data;
};

const getCreativeFatigueMap = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignFatigueMapRO[]> => {
  const { data } = await apiService.get<GetProgrammaticCampaignFatigueMapRO[]>(
    IP.FatigueMap(id),
    withFiltersParams(filters)
  );
  return data;
};

const getOverallVideoViews = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignOverallVideoViewsRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignOverallVideoViewsRO>(
      IP.OverallVideoMap(id),
      withFiltersParams(filters)
    );
  return data;
};

const getVideoCompletionRate = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignVideoCompletionRateRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignVideoCompletionRateRO>(
      IP.VideoCompletionRate(id),
      withFiltersParams(filters)
    );
  return data;
};

const getEngagementTestingResults = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignCreativeEngagementTestingRO> => {
  const { data } = await apiService.get(
    IP.EngagementTestingResults(id),
    withFiltersParams(filters)
  );
  return data;
};

export const innovidRequests: DisplayPerformanceRequests = {
  campaignEngagement: {
    getStatistics,
    getImpressionsByLocation,
    getTopicsByLocation,
    getPerformanceByDevice,
    getMobileDeliveryBreakdown,
    getSiteEngagementLift,
  },
  accountsPerformance: {
    getStatistics,
    getIntegratedCampaignReport,
    getAccountLifts,
    getAccountInBuyerResearchStage,
    getTopAccounts,
  },
  creativeEngagement: {
    getCreativeWithHighestEngagement,
    getBuyerResearchStageEngagement,
    getCreativeFatigueMap,
    getOverallVideoViews,
    getVideoCompletionRate,
    getEngagementTestingResults,
  },
};
